import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { SearchableSelect } from '../components/SearchableSelect';
import { FileText, FileSpreadsheet, ReceiptText, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };

const EMPTY = { date_from: '', date_to: '', supplier_id: '', expense_concept_id: '', q: '' };

export default function PurchaseFiscalDocuments() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ rows: [], totals: {}, count: 0, by_concept: {}, available_concepts: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/suppliers`, { withCredentials: true })
      .then(r => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  const params = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/purchases/fiscal-documents`,
        { params: params(), withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error('Error al cargar los comprobantes'); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openExport = (ext) => {
    const qs = new URLSearchParams({ ...params(), t: Date.now() }).toString();
    window.open(`${BACKEND_URL}/api/purchases/fiscal-documents.${ext}?${qs}`, '_blank');
  };

  const t = data.totals || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-sky-100 rounded-lg"><ReceiptText className="w-6 h-6 text-sky-700" /></div>
          <div>
            <h1 className="text-2xl font-bold">Comprobantes Fiscales de Proveedores</h1>
            <p className="text-sm text-muted-foreground">Detalle impositivo e imputación (concepto de gasto o cuenta contable)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => openExport('pdf')} data-testid="pfd-pdf-btn">
            <FileText className="w-4 h-4 mr-1" /> Exportar PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => openExport('xlsx')} data-testid="pfd-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar Excel
          </Button>
        </div>
      </div>

      <Card><CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="pfd-date-from" />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="pfd-date-to" />
          </div>
          <div>
            <Label className="text-xs">Proveedor</Label>
            <SearchableSelect
              value={filters.supplier_id}
              onChange={(v) => setFilters({ ...filters, supplier_id: v })}
              options={suppliers.map(s => ({ value: s.supplier_id, label: s.business_name, hint: s.tax_id ? `CUIT ${s.tax_id}` : undefined }))}
              placeholder="Todos los proveedores"
              emptyLabel="Ningún proveedor coincide"
              testId="pfd-supplier"
            />
          </div>
          <div>
            <Label className="text-xs">Concepto de gasto</Label>
            <SearchableSelect
              value={filters.expense_concept_id}
              onChange={(v) => setFilters({ ...filters, expense_concept_id: v })}
              options={(data.available_concepts || []).map(c => ({ value: c.concept_id, label: c.name }))}
              placeholder="Todos los conceptos"
              emptyLabel="Ningún concepto coincide"
              testId="pfd-concept"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-xs">Buscar</Label>
              <Input value={filters.q} placeholder="Proveedor, CUIT o imputación"
                onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="pfd-search" />
            </div>
            <Button variant="ghost" size="icon" className="mt-5" title="Limpiar filtros"
              onClick={() => setFilters(EMPTY)} data-testid="pfd-clear-btn">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['Comprobantes', data.count, false], ['Neto gravado', t.neto_gravado, true],
          ['IVA', t.iva, true], ['Exento / No gravado', t.exento, true], ['Total', t.total, true]].map(([label, val, money]) => (
          <Card key={label}><CardContent className="pt-4">
            <div className="text-[11px] text-muted-foreground uppercase">{label}</div>
            <div className="text-lg font-bold tabular-nums">{money ? fmt(val) : (val ?? 0)}</div>
          </CardContent></Card>
        ))}
      </div>

      {Object.keys(data.by_concept || {}).length > 0 && (
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground mb-2 uppercase">Totales por imputación</p>
          <div className="flex flex-wrap gap-2" data-testid="pfd-by-concept">
            {Object.entries(data.by_concept).map(([k, v]) => (
              <Badge key={k} variant="outline" className="text-xs py-1">
                {k}: <span className="font-semibold ml-1">{fmt(v.total)}</span>
                <span className="text-muted-foreground ml-1">({v.count})</span>
              </Badge>
            ))}
          </div>
        </CardContent></Card>
      )}

      <Card><CardContent className="pt-6">
        <Table data-testid="pfd-table">
          <TableHeader><TableRow>
            <TableHead>Fecha</TableHead><TableHead>Comprobante</TableHead><TableHead>Proveedor</TableHead>
            <TableHead>Imputación</TableHead><TableHead>Origen</TableHead>
            <TableHead className="text-right">Neto</TableHead><TableHead className="text-right">IVA</TableHead>
            <TableHead className="text-right">Exento</TableHead><TableHead className="text-right">Percep.</TableHead>
            <TableHead className="text-right">Total</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">Cargando...</TableCell></TableRow>
            ) : data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8" data-testid="pfd-empty">Sin comprobantes para los filtros aplicados</TableCell></TableRow>
            ) : data.rows.map(r => (
              <TableRow key={r.id} data-testid={`pfd-row-${r.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                <TableCell className="text-xs font-mono">{r.comprobante}</TableCell>
                <TableCell className="text-xs">
                  {r.name}
                  <span className="block text-[10px] text-muted-foreground">{r.tax_id}</span>
                </TableCell>
                <TableCell className="text-xs">{r.imputation}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{r.origin}</Badge></TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(r.neto_gravado)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(r.iva)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(r.exento)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(r.percepciones)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums font-semibold">{fmt(r.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
